import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import * as orgApi from '../api/organizations';
import * as adminApi from '../api/admin';
import * as usersApi from '../api/users';
import * as billingApi from '../api/billing';
import Tabs from '../components/Tabs';
import SecuritySettingsPanel from '../components/SecuritySettingsPanel';
import PolicySettingsPanel from '../components/PolicySettingsPanel';
import ComplianceReviewPanel from '../components/ComplianceReviewPanel';
import MemberServicesAdminPanel from '../components/MemberServicesAdminPanel';
import MarketplaceCategoriesPanel from '../components/MarketplaceCategoriesPanel';
import PlatformFeesPanel from '../components/PlatformFeesPanel';
import { useConfirm } from '../lib/confirm';
import { useToast } from '../components/toast';
import {
  Spinner,
  ErrorAlert,
  EmptyState,
  StatusPill,
  Field,
  SelectField,
  formatDate,
} from '../components/ui';
import { ui, cn } from '../lib/ui';
import { ORG_TYPES, ORG_TYPE_POLICIES } from '../lib/orgTypes';
import { NONPROFIT_CHANGE_WARNING, afterTypeChange } from '../lib/typeChange';

const TABS = [
  { id: 'orgs', label: 'Organizations' },
  { id: 'users', label: 'Users' },
  { id: 'tiers', label: 'Service Tiers' },
  { id: 'policies', label: 'Policies' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'services', label: 'Member services' },
  { id: 'marketplace', label: 'Marketplace' },
  { id: 'security', label: 'Security' },
];

export default function PlatformAdmin() {
  const [tab, setTab] = useState('orgs');
  const { user } = useAuth();

  if (user?.platformRole !== 'platform-admin') {
    return (
      <div className={ui.alertError}>
        Only platform administrators can access this page.
      </div>
    );
  }

  return (
    <>
      <h1 className={ui.h1}>Platform Administration</h1>
      <Tabs tabs={TABS} value={tab} onChange={setTab} idPrefix="platform-admin" />
      {tab === 'orgs' && <OrganizationsPanel />}
      {tab === 'users' && <UsersPanel />}
      {tab === 'tiers' && <TiersPanel />}
      {tab === 'policies' && <PolicySettingsPanel />}
      {tab === 'compliance' && <ComplianceReviewPanel />}
      {tab === 'services' && <MemberServicesAdminPanel />}
      {tab === 'marketplace' && (
        <section>
          <h2 className="mt-0 text-title-m">Platform categories</h2>
          <p className={cn(ui.hint, 'mb-3')}>
            The documented category list organizations import from (docs/Decisions.md D68). Each
            category carries a review policy and the requirements vendors must meet; mark the ones
            investment organizations may use. Fee limits are under Policies.
          </p>
          <MarketplaceCategoriesPanel owner="platform" />
          <PlatformFeesPanel />
        </section>
      )}
      {tab === 'security' && <SecuritySettingsPanel />}
    </>
  );
}

function OrganizationsPanel() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const confirm = useConfirm();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await orgApi.listOrganizations({ status: statusFilter, limit: 50 });
      setOrgs(data.organizations || []);
      setCursor(data.nextCursor || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleStatusChange(org, newStatus) {
    const action = newStatus === 'suspended' ? 'Suspend' : 'Activate';
    const ok = await confirm({
      title: `${action} ${org.name}?`,
      message: newStatus === 'suspended'
        ? 'Members will not be able to access this organization while suspended.'
        : 'This will restore member access to the organization.',
      danger: newStatus === 'suspended',
    });
    if (!ok) return;
    try {
      await orgApi.updateOrganization(org.orgId, { status: newStatus });
      toast.success(`${org.name} ${newStatus === 'suspended' ? 'suspended' : 'activated'}`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  // docs/Decisions.md D64: once an org has members only a platform admin
  // changes its type; a different type drops the profile it had started.
  async function handleTypeChange(org, orgType) {
    if (!orgType || orgType === org.orgType) return;
    const label = ORG_TYPE_POLICIES[orgType].label;
    const ok = await confirm({
      title: `Make ${org.name} a ${label.toLowerCase()} organization?`,
      message: `What it can take payments for changes at once. A profile for another type is cleared, and the organization must complete and submit the new one.${org.orgType === 'nonprofit' ? ` ${NONPROFIT_CHANGE_WARNING}` : ''}`,
      danger: true,
    });
    if (!ok) return;
    try {
      await orgApi.updateOrganization(org.orgId, { orgType });
      toast.success(`${org.name} is now a ${label.toLowerCase()} organization`);
      const note = await afterTypeChange(org.orgId, org.orgType, orgType);
      if (note) toast.info(note);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleDelete(org) {
    const ok = await confirm({
      title: `Delete ${org.name}?`,
      message: 'This soft-deletes the organization. Members will lose access.',
      danger: true,
    });
    if (!ok) return;
    try {
      await orgApi.deleteOrganization(org.orgId);
      toast.success(`${org.name} deleted`);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function loadMore() {
    if (!cursor) return;
    try {
      const data = await orgApi.listOrganizations({ status: statusFilter, limit: 50, cursor });
      setOrgs(prev => [...prev, ...(data.organizations || [])]);
      setCursor(data.nextCursor || null);
    } catch (err) {
      toast.error(err.message || 'Failed to load more organizations');
    }
  }

  if (loading) return <Spinner label="Loading organizations..." />;
  if (error) return <ErrorAlert error={error} />;

  return (
    <section>
      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <h2 className="m-0 text-title-m">Organizations</h2>
        <div className="flex flex-wrap items-center gap-2">
          <SelectField
            label="Status"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ marginBottom: 0 }}
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="deleted">Deleted</option>
          </SelectField>
          <Link to="/orgs/new" className={ui.btnPrimary}>
            Create organization
          </Link>
        </div>
      </div>

      {orgs.length === 0 ? (
        <EmptyState>No organizations found with status &ldquo;{statusFilter}&rdquo;.</EmptyState>
      ) : (
        <div className={cn(ui.card, ui.tableWrap)}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Tier</th>
                <th>Type</th>
                <th>Members</th>
                <th>Status</th>
                <th>Created</th>
                <th className="text-right!">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map(org => (
                <tr key={org.orgId}>
                  <td>
                    <Link to={`/orgs/${org.orgId}`}>{org.name}</Link>
                  </td>
                  <td><code>{org.slug}</code></td>
                  <td>{org.tier}</td>
                  <td>
                    <select
                      className={cn(ui.input, 'w-auto! py-1!')}
                      aria-label={`Type of ${org.name}`}
                      value={org.orgType || ''}
                      onChange={e => handleTypeChange(org, e.target.value)}
                      disabled={org.status === 'deleted'}
                    >
                      {!org.orgType && <option value="">Not chosen</option>}
                      {ORG_TYPES.map(type => (
                        <option key={type} value={type}>
                          {ORG_TYPE_POLICIES[type].label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{org.memberCount ?? 0}</td>
                  <td><StatusPill status={org.status} /></td>
                  <td>{formatDate(org.createdAt)}</td>
                  <td className="text-right!">
                    <div className="flex justify-end gap-1">
                      {org.status === 'active' && (
                        <button
                          className={cn(ui.btnSecondary, ui.btnSm)}
                          onClick={() => handleStatusChange(org, 'suspended')}
                        >
                          Suspend
                        </button>
                      )}
                      {org.status === 'suspended' && (
                        <button
                          className={cn(ui.btnPrimary, ui.btnSm)}
                          onClick={() => handleStatusChange(org, 'active')}
                        >
                          Activate
                        </button>
                      )}
                      {org.status !== 'deleted' && (
                        <button
                          className={cn(ui.btnDanger, ui.btnSm)}
                          onClick={() => handleDelete(org)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cursor && (
        <div className="mt-3 text-center">
          <button className={ui.btnSecondary} onClick={loadMore}>
            Load more organizations
          </button>
        </div>
      )}
    </section>
  );
}

function UsersPanel() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const confirm = useConfirm();
  const toast = useToast();

  async function handleSearch(e) {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.searchUsers({ emailPrefix: query.trim() });
      setResults(data.users || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleTogglePlatformRole(user) {
    const granting = !user.platformRole;
    const ok = await confirm({
      title: granting
        ? `Grant platform-admin to ${user.firstName} ${user.lastName}?`
        : `Revoke platform-admin from ${user.firstName} ${user.lastName}?`,
      message: granting
        ? 'This user will be able to manage all organizations, tiers, and users.'
        : 'This user will lose platform-wide administration privileges.',
      danger: !granting,
    });
    if (!ok) return;
    try {
      await usersApi.updateUser(user.userId, {
        platformRole: granting ? 'platform-admin' : null,
      });
      setResults(prev =>
        prev.map(u =>
          u.userId === user.userId
            ? { ...u, platformRole: granting ? 'platform-admin' : null }
            : u
        )
      );
      toast.success(`${user.firstName} ${user.lastName} ${granting ? 'granted' : 'revoked'} platform-admin`);
    } catch (err) {
      toast.error(err.message || 'Failed to update platform role');
    }
  }

  return (
    <section>
      <h2 className={ui.h2}>User Search</h2>
      <form onSubmit={handleSearch} className="mb-4 flex flex-wrap items-center gap-2 *:min-w-0">
        <Field
          label="Search by email"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="user@example.com"
          style={{ flex: 1, marginBottom: 0 }}
        />
        <button type="submit" className={ui.btnPrimary} disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && <ErrorAlert error={error} />}

      {results && results.length === 0 && (
        <EmptyState>No users found matching &ldquo;{query}&rdquo;.</EmptyState>
      )}

      {results && results.length > 0 && (
        <div className={cn(ui.card, ui.tableWrap)}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Platform Role</th>
                <th>Status</th>
                <th className="text-right!">Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.map(u => (
                <tr key={u.userId}>
                  <td>
                    {u.firstName} {u.lastName}
                  </td>
                  <td>{u.email}</td>
                  <td>{u.role}</td>
                  <td>
                    {u.platformRole ? <StatusPill status={u.platformRole} /> : <span className={ui.muted}>-</span>}
                  </td>
                  <td>
                    <StatusPill status={u.isActive ? 'active' : 'inactive'} />
                  </td>
                  <td className="text-right!">
                    <button
                      className={cn(u.platformRole ? ui.btnDanger : ui.btnSecondary, ui.btnSm)}
                      onClick={() => handleTogglePlatformRole(u)}
                    >
                      {u.platformRole ? 'Revoke admin' : 'Grant admin'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const ALL_FEATURES = [
  'video-hosting', 'events', 'calendar', 'groups', 'inbox', 'forums',
  'verification', 'custom-roles', 'saml-sso', 'api-access',
  'ribbon-designer', 'badge-visibility', 'site-upload', 'payments-advanced',
];

const FEATURE_LABELS = {
  'video-hosting': 'Video hosting',
  events: 'Events',
  calendar: 'Calendar',
  groups: 'Groups',
  inbox: 'Inbox (messaging)',
  forums: 'Forums',
  verification: 'Verification workflows',
  'custom-roles': 'Custom roles',
  'saml-sso': 'SAML SSO',
  'api-access': 'API access',
  'ribbon-designer': 'Ribbon and badge designer (premium)',
  'badge-visibility': 'Ribbon and badge visibility scopes (premium)',
  'site-upload': 'Hosted site upload (premium)',
  'payments-advanced': 'Advanced payments (premium)',
};

function TierEditor({ tier, onSave, saving }) {
  const [form, setForm] = useState({
    name: tier.name || '',
    stripePriceId: tier.stripePriceId || '',
    transactionFeeRate: tier.transactionFeeRate ?? 0,
    features: tier.features || [],
    maxMembers: tier.limits?.maxMembers ?? 50,
    maxVideos: tier.limits?.maxVideos ?? 100,
    maxStorageGb: tier.limits?.maxStorageGb ?? 10,
  });

  function toggleFeature(feat) {
    setForm(p => ({
      ...p,
      features: p.features.includes(feat)
        ? p.features.filter(f => f !== feat)
        : [...p.features, feat],
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSave(tier.tierId, {
      name: form.name,
      features: form.features,
      limits: {
        maxMembers: parseInt(form.maxMembers, 10) || -1,
        maxVideos: parseInt(form.maxVideos, 10) || -1,
        maxStorageGb: parseInt(form.maxStorageGb, 10) || 10,
      },
      stripePriceId: form.stripePriceId || undefined,
      transactionFeeRate: parseFloat(form.transactionFeeRate) || 0,
    });
  }

  return (
    <form onSubmit={handleSubmit} className={cn(ui.card, 'mb-4')}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="m-0 text-title-s">{tier.tierId.toUpperCase()}</h3>
        <button type="submit" className={cn(ui.btnPrimary, ui.btnSm)} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <div className="mb-3 grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] gap-3">
        <Field label="Display name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required style={{ marginBottom: 0 }} />
        <Field label="Stripe Price ID" value={form.stripePriceId} onChange={e => setForm(p => ({ ...p, stripePriceId: e.target.value }))} placeholder="price_..." style={{ marginBottom: 0 }} />
        <Field label="Transaction fee (%)" type="number" min="0" max="100" step="0.01" value={form.transactionFeeRate} onChange={e => setForm(p => ({ ...p, transactionFeeRate: e.target.value }))} style={{ marginBottom: 0 }} />
      </div>

      <div className="mb-3 grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] gap-3">
        <Field label="Max members (-1 = unlimited)" type="number" min="-1" value={form.maxMembers} onChange={e => setForm(p => ({ ...p, maxMembers: e.target.value }))} style={{ marginBottom: 0 }} />
        <Field label="Max videos (-1 = unlimited)" type="number" min="-1" value={form.maxVideos} onChange={e => setForm(p => ({ ...p, maxVideos: e.target.value }))} style={{ marginBottom: 0 }} />
        <Field label="Storage (GB)" type="number" min="1" value={form.maxStorageGb} onChange={e => setForm(p => ({ ...p, maxStorageGb: e.target.value }))} style={{ marginBottom: 0 }} />
      </div>

      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2 text-body-s font-semibold">Features</legend>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-1">
          {ALL_FEATURES.map(feat => (
            <label key={feat} className="flex cursor-pointer items-center gap-2 text-body-s">
              <input type="checkbox" className={ui.checkbox} checked={form.features.includes(feat)} onChange={() => toggleFeature(feat)} />
              {FEATURE_LABELS[feat] || feat}
            </label>
          ))}
        </div>
      </fieldset>
    </form>
  );
}

function TiersPanel() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await billingApi.listTiers();
      setTiers(data.tiers || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(tierId, data) {
    setSavingId(tierId);
    try {
      await billingApi.updateTier(tierId, data);
      toast.success(`${tierId} tier updated`);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to update tier');
    } finally {
      setSavingId(null);
    }
  }

  if (loading) return <Spinner label="Loading tiers..." />;
  if (error) return <ErrorAlert error={error} />;

  return (
    <section>
      <h2 className={ui.h2}>Service Tier Configuration</h2>
      <p className={cn(ui.muted, 'mb-4 text-body-s')}>
        Configure the features, limits, and pricing for each service tier. Changes apply to all organizations on that tier.
      </p>
      {tiers.map(tier => (
        <TierEditor
          key={tier.tierId}
          tier={tier}
          onSave={handleSave}
          saving={savingId === tier.tierId}
        />
      ))}
    </section>
  );
}
